import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { ProfileSettingsState, ProfileData } from './types';
import { endpoints, formatErrors, baseQuery } from 'utils/api/endpoints';
import { ApiResponse } from 'types/ApiResponse';

export const initialState: ProfileSettingsState = {
  profileData: null,
  isLoading: false,
  error: null,
  isUpdatingGeneral: false,
  isUpdatingWebsite: false,
  generalError: null,
  websiteError: null,
  isChangePasswordModalOpen: false,
  isChangingPassword: false,
  changePasswordError: null,
};

export const slice = createSlice({
  name: 'profileSettings',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setProfileData(state, action: PayloadAction<ProfileData>) {
      state.profileData = action.payload;
    },
    setUpdatingGeneral(state, action: PayloadAction<boolean>) {
      state.isUpdatingGeneral = action.payload;
    },
    setUpdatingWebsite(state, action: PayloadAction<boolean>) {
      state.isUpdatingWebsite = action.payload;
    },
    setGeneralError(state, action: PayloadAction<string | null>) {
      state.generalError = action.payload;
    },
    setWebsiteError(state, action: PayloadAction<string | null>) {
      state.websiteError = action.payload;
    },
    setChangePasswordModalOpen(state, action: PayloadAction<boolean>) {
      state.isChangePasswordModalOpen = action.payload;
    },
    setChangingPassword(state, action: PayloadAction<boolean>) {
      state.isChangingPassword = action.payload;
    },
    setChangePasswordError(state, action: PayloadAction<string | null>) {
      state.changePasswordError = action.payload;
    },
    clearErrors(state) {
      state.error = null;
      state.generalError = null;
      state.websiteError = null;
      state.changePasswordError = null;
    },
  },
});

export const { actions: profileSettingsActions } = slice;

// Request/Response types
interface UpdateGeneralInfoRequest {
  firstName: string;
  lastName: string;
}

interface UpdateWebsiteSettingsRequest {
  name: string;
  domain: string;
  timezone: string;
}

interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export const api = createApi({
  reducerPath: 'profileSettingsApi',
  baseQuery,
  tagTypes: ['Profile'],
  endpoints: build => ({
    getProfile: build.query<ApiResponse<ProfileData>, void>({
      query: () => ({
        url: endpoints.getCompanyUserProfile.url,
        method: endpoints.getCompanyUserProfile.method,
      }),
      providesTags: ['Profile'],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    updateProfile: build.mutation<ApiResponse<ProfileData>, UpdateGeneralInfoRequest>({
      query: (profileData) => ({
        url: endpoints.updateCompanyUserProfile.url,
        method: endpoints.updateCompanyUserProfile.method,
        body: profileData,
      }),
      invalidatesTags: ['Profile'],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    updateWebsiteSettings: build.mutation<ApiResponse<ProfileData>, UpdateWebsiteSettingsRequest>({
      query: (websiteData) => ({
        url: endpoints.updateWebsiteSettings.url,
        method: endpoints.updateWebsiteSettings.method,
        body: websiteData,
      }),
      invalidatesTags: ['Profile'],
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
    changePassword: build.mutation<ApiResponse<{ message: string }>, ChangePasswordRequest>({
      query: (passwordData) => ({
        url: endpoints.companyUserChangePassword.url,
        method: endpoints.companyUserChangePassword.method,
        body: passwordData,
      }),
      transformErrorResponse(baseQueryReturnValue, meta, arg) {
        return formatErrors(baseQueryReturnValue.data);
      },
    }),
  }),
});

export const useProfileSettingsSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  
  return {
    actions: profileSettingsActions,
    // Profile queries and mutations
    useGetProfileQuery: api.useGetProfileQuery,
    useLazyGetProfileQuery: api.useLazyGetProfileQuery,
    useUpdateProfileMutation: api.useUpdateProfileMutation,
    useUpdateWebsiteSettingsMutation: api.useUpdateWebsiteSettingsMutation,
    useChangePasswordMutation: api.useChangePasswordMutation,
  };
}; 